import { SplitFactory } from '@splitsoftware/splitio';
import type { PropsWithChildren } from 'react';
import React, { useCallback, useMemo, createContext } from 'react';
import { nanoid } from 'nanoid';

import { loadMockedFeatures } from './mockedFeatures';
import { isBrowser } from '../../utils/environment';

type SplitContext = {
  attributes: SplitIO.Attributes;
  anonymousId?: string;
  client: Promise<SplitIO.IClient | undefined> | null;
};

const defaultValue: SplitContext = {
  attributes: {},
  client: null,
};

const context = createContext(defaultValue);

const generateAnonymousId = () => `anonymous-${nanoid()}`;

interface SplitProps extends PropsWithChildren<{}> {}

const SplitProvider = ({ children }: SplitProps) => {
  const splitApiKey = process.env.GATSBY_SPLIT_IO_AUTH_KEY;
  const [splitFactory, anonymousId] = useMemo(() => {
    if (!splitApiKey) return [null, ''];

    const key = generateAnonymousId();

    /**
     * @see https://help.split.io/hc/en-us/articles/360020448791-JavaScript-SDK#localhost-mode
     */
    const features =
      isBrowser() && splitApiKey === 'localhost'
        ? loadMockedFeatures()
        : undefined;

    const factory = SplitFactory({
      core: {
        authorizationKey: splitApiKey,
        key,
      },
      features,
      startup: {
        readyTimeout: 20,
      },
    });

    return [factory, key];
  }, [splitApiKey]);

  const client = splitFactory?.client();

  // Keep the client promise the same across re-renders
  const getClient = useCallback(async () => {
    try {
      // When using async/await syntax with the split client, the rejection
      // should be explicitly propagated or the promise will hang.
      // More details found at https://github.com/splitio/javascript-commons/blob/39af1a1/src/readiness/sdkReadinessManager.ts#L95-L107
      await client?.ready().catch(e => {
        throw e;
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Split client failed to initialize', error);
    }

    return client;
  }, [client]);

  const contextValue: SplitContext = {
    attributes: {},
    anonymousId,
    client: getClient(),
  };

  return <context.Provider value={contextValue}>{children}</context.Provider>;
};

export { SplitProvider, context as splitContext };
